import React, { useState } from 'react';
import { useTranslations } from 'next-intl';

import { Courseversion } from '@/payload/payload-types';

import ChevronCircleDown from '../../_icons/Chevron-circle-down';
import RichTextLexical from '../RichTextLexical';

import getSubTopicContent from './get-subtopic-content';
import { logToPayload } from '../../_utilities/log-to-payload';

export const CoursePlan: React.FC<{
	courseversion: Courseversion;
	planItems: Courseversion['plan']['topics'];
	periodChoice: 'day' | 'week' | 'session';
}> = ({ planItems, periodChoice, courseversion }) => {
	const t = useTranslations('Course');
	const [activePlanSections, setActivePlanSections] = useState<string[]>([]);
	return (
		<div className="mb-12 space-y-2">
			{planItems?.map(plan => {
				if (!plan.id || typeof plan === 'string') {
					logToPayload('Bad course plan depth, courseversionId: ', courseversion.id);
					throw new Error('Something went wrong!');
				}
				const isActiveSection = activePlanSections.includes(plan.id);
				const onTitleClick = () => {
					if (!plan.id || typeof plan === 'string') {
						logToPayload('Bad course plan depth, courseversionId: ', courseversion.id);
						throw new Error('Something went wrong!');
					}

					if (isActiveSection) {
						const newActivePlanSections = activePlanSections.filter(
							a => a !== plan?.id,
						);
						setActivePlanSections(newActivePlanSections);
					} else {
						setActivePlanSections([...activePlanSections, plan.id]);
					}
				};
				const getAllSubTopicLecturerNames = () => {
					if (!plan.subTopics) return [];

					return plan.subTopics
						.flatMap(subTopic => subTopic.subTopicLecturers || [])
						.map(lecturer => {
							if (typeof lecturer === 'string') {
								logToPayload(
									`Bad depth for subtopiclecturer: lecturer: ${lecturer}, courseversionId: ${courseversion.id}`,
								);
								return '';
							}
							return lecturer.name;
						})
						.reduce((uniqueNames: string[], name: string) => {
							if (!uniqueNames.includes(name)) {
								uniqueNames.push(name);
							}
							return uniqueNames;
						}, [] as string[]);
				};

				const allSubTopicLecturerNames = getAllSubTopicLecturerNames();
				return (
					<div
						className="group border-gold-light cursor-pointer space-y-2 rounded-2xl border transition-all"
						key={plan.id}
						onClick={onTitleClick}
					>
						<div
							className={`group-hover:bg-gold-light-8 ${isActiveSection ? 'rounded-t-2xl' : 'rounded-2xl'}`}
						>
							<div className="relative flex flex-col items-start px-8 py-4 lg:flex-row lg:space-x-4">
								<div className="text-gold-light pt-2 text-sm font-bold tracking-wide text-nowrap uppercase">
									{`${t('course-plan-timeperiod', { timePeriod: periodChoice })} ${plan.sequence}`}
								</div>
								<ChevronCircleDown
									className={`absolute right-[16px] transition-transform ${isActiveSection ? 'rotate-180' : ''}`}
								/>
								<div className="flex flex-col">
									<RichTextLexical
										className="text-muted-navy w-fit pr-12 leading-8 font-bold"
										content={plan.titleRichTextLexical}
									/>

									<div className="text-brand-medium-grey text-sm leading-6 font-medium">
										{allSubTopicLecturerNames.length > 0 && (
											<div>{allSubTopicLecturerNames.join(', ')}</div>
										)}
									</div>
								</div>
							</div>
						</div>

						<div
							className={`text-navy ${isActiveSection ? 'block px-8 py-4' : 'hidden'}`}
						>
							<RichTextLexical
								className="pb-4"
								content={plan.descriptionRichTextLexical}
							/>

							{plan.subTopics && (
								<ul>
									{plan.subTopics.map(subTopic => {
										const subTopicLecturers = subTopic.subTopicLecturers?.map(
											lecturer => {
												if (typeof lecturer === 'string') {
													logToPayload(
														`Bad depth for subtopiclecturer: lecturer: ${lecturer}, courseversionId: ${courseversion.id}`,
													);

													return '';
												}

												return lecturer.name;
											},
										);

										return (
											<li
												key={`subTopic-${subTopic.id}`}
												className="flex items-center"
											>
												<div>
													{subTopic.subTopicDescriptionRichTextLexical && (
														<div>
															<RichTextLexical
																content={
																	subTopicLecturers?.length
																		? getSubTopicContent(
																				subTopic.subTopicDescriptionRichTextLexical,
																				` (${subTopicLecturers?.join(', ')})`,
																			)
																		: subTopic.subTopicDescriptionRichTextLexical
																}
															/>
														</div>
													)}
												</div>
											</li>
										);
									})}
								</ul>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
